import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Badge,
  Card,
  Divider,
  Page,
  Stack,
  Text,
  TileButtonActionList,
  VStack,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { StockCountLine } from "@towersystems/roam-common/lib/generated-types";
import { useCallback } from "react";
import { FlatList } from "react-native";
import { Spacings, View } from "react-native-ui-lib";
import { StockCountCompletedRootParamList } from "../../../../types";
import { statusToCompletionStatus } from "../../../../utilities/stock-count";
import { StatusBadge } from "../../components";
import { useStockCountCompletedContext } from "../../utils/stocktake-completed/hooks";
import { StockCountListLineItem } from "./components";
import colors from "react-native-ui-lib/src/style/colors";
import { useDownloadStockCount } from "../../../../utilities/stock-count/use-download-stock-count";

export type StockCountCompletedScreenNavigationProp = NativeStackScreenProps<
  StockCountCompletedRootParamList,
  "StockCountCompletedScreen"
>;

export interface StockCountCompletedScreenProps
  extends StockCountCompletedScreenNavigationProp {}

export function StockCountCompletedScreen({
  navigation,
}: StockCountCompletedScreenProps) {
  const { stockCount, stockCountLines } = useStockCountCompletedContext();

  const completionStatus = statusToCompletionStatus(stockCount.status);
  const { downloadStockCount, loading: loadingCSV } = useDownloadStockCount();
  const flashMessagesContext = useFlashMessages();

  const renderStockCount = useCallback(({ item }: { item: StockCountLine }) => {
    return (
      <StockCountListLineItem
        onPress={() =>
          navigation.navigate("StockCountCompletedCountHistoryScreen", {
            lineId: item.id,
          })
        }
        stockCountLine={item}
      />
    );
  }, []);

  const handleDownloadCSV = async () => {
    return downloadStockCount(stockCount.id).catch((e) => {
      console.log(e);
      flashMessagesContext.showMessage({
        severity: "error",
        message: "Failed to download stock count",
      });
    });
  };

  const headerMarkup = (
    <View
      style={{
        flexDirection: "row",
        borderBottomWidth: 2,
        borderBottomColor: colors.borderColor,
      }}
    >
      <View style={{ flexGrow: 1, padding: Spacings.s4 }}>
        <Text></Text>
      </View>
      <View
        style={{ width: 100, alignItems: "flex-end", padding: Spacings.s4 }}
      >
        <Text>Expected</Text>
      </View>
      <View
        style={{ width: 100, alignItems: "flex-end", padding: Spacings.s4 }}
      >
        <Text>Counted</Text>
      </View>
    </View>
  );

  const sourceBadge =
    stockCount.source === "INTERNAL" ? "SOURCE_ROAM" : "SOURCE_RETAILER";

  const actions = [];

  actions.push({
    id: "csv",
    isAction: true,
    label: "Download CSV",
    onPress: handleDownloadCSV,
    disabled: loadingCSV,
  });

  return (
    <Page
      title={stockCount.name}
      badges={
        <Stack spacing={Spacings.s3}>
          <Badge countSource={sourceBadge} />
          <StatusBadge status={completionStatus}>
            {completionStatus}
          </StatusBadge>
        </Stack>
      }
    >
      <View>
        <VStack>
          {actions.length >= 1 ? (
            <View>
              <TileButtonActionList actions={actions} />
            </View>
          ) : null}
          {stockCountLines.length >= 1 ? (
            <View>
              {headerMarkup}
              <FlatList
                windowSize={3}
                initialNumToRender={10}
                ItemSeparatorComponent={() => <Divider marginVertical={0} />}
                data={stockCountLines}
                keyExtractor={(item) => item.id}
                renderItem={renderStockCount}
              />
            </View>
          ) : undefined}
        </VStack>
      </View>
    </Page>
  );
}
